import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/store';
import {
  createAdminVisitedTab,
  findDefaultAdminVisitedTab,
  refreshAdminVisitedTab,
  upsertAdminVisitedTab,
  type AdminVisitedTab,
} from './visitedTabs';

export function useAdminVisitedTabs() {
  const principal = useAdminAuth(state => state.principal);
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState<AdminVisitedTab[]>([]);
  const initializedPrincipalIdRef = useRef<number | null>(null);
  const defaultTab = useMemo(
    () => findDefaultAdminVisitedTab(hasPermission),
    [hasPermission, principal?.authz_version],
  );
  const currentTab = useMemo(
    () => createAdminVisitedTab(location.pathname, location.search, location.hash),
    [location.hash, location.pathname, location.search],
  );
  const canAccessCurrentTab = Boolean(
    currentTab
    && refreshAdminVisitedTab(currentTab, hasPermission),
  );

  // 页签只保存在当前 SPA 会话内，不写 localStorage；浏览器刷新后由当前 URL 重新初始化。
  useEffect(() => {
    const principalId = principal?.user.id ?? null;
    if (!principalId) {
      initializedPrincipalIdRef.current = null;
      setTabs([]);
      return;
    }
    if (initializedPrincipalIdRef.current === principalId) return;

    initializedPrincipalIdRef.current = principalId;
    try {
      window.localStorage.removeItem(`wjs-admin:visited-tabs:${principalId}`);
    } catch {
      // 清理旧版本页签缓存失败不影响当前会话使用。
    }
    const initialTab = currentTab && canAccessCurrentTab
      ? { ...currentTab, closable: currentTab.id !== defaultTab?.id }
      : defaultTab;
    setTabs(initialTab ? [initialTab] : []);
  }, [canAccessCurrentTab, currentTab, defaultTab, principal?.user.id]);

  useEffect(() => {
    if (!principal?.user.id || !currentTab || !canAccessCurrentTab) return;
    setTabs(current => {
      const next = upsertAdminVisitedTab(current, currentTab);
      return next.map(tab => (
        tab.id === defaultTab?.id ? { ...tab, closable: false } : tab
      ));
    });
  }, [
    canAccessCurrentTab,
    currentTab?.href,
    currentTab?.id,
    currentTab?.label,
    defaultTab?.id,
    principal?.user.id,
  ]);

  useEffect(() => {
    if (!principal?.user.id) return;
    setTabs(current => current.flatMap(tab => {
      const refreshed = refreshAdminVisitedTab(tab, hasPermission);
      if (!refreshed) return [];
      return [{
        ...refreshed,
        closable: refreshed.id !== defaultTab?.id,
      }];
    }));
  }, [defaultTab?.id, hasPermission, principal?.authz_version, principal?.user.id]);

  const activateTab = useCallback((tab: AdminVisitedTab) => {
    if (`${location.pathname}${location.search}${location.hash}` !== tab.href) {
      navigate(tab.href);
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  const closeTab = useCallback((tabId: string) => {
    const closingIndex = tabs.findIndex(tab => tab.id === tabId);
    const closingTab = tabs[closingIndex];
    if (!closingTab?.closable) return;

    const next = tabs.filter(tab => tab.id !== tabId);
    const fallbackIndex = Math.max(0, Math.min(closingIndex - 1, next.length - 1));
    const fallback = next[fallbackIndex] ?? defaultTab;
    if (location.pathname === tabId && !fallback) return;

    setTabs(next);
    if (location.pathname === tabId && fallback) navigate(fallback.href);
  }, [defaultTab, location.pathname, navigate, tabs]);

  return {
    tabs,
    activeTabId: location.pathname,
    activateTab,
    closeTab,
  };
}
